/**
 * RuntimeToggleable — optional capability for behaviors whose effect can be
 * switched on and off at runtime by the behavior graph
 * (trace/setBehaviorEnabled), independent of attach/detach.
 *
 * Contract:
 * - The authored `enabled` field on the behavior's data is the initial state;
 *   every play-mode start resets the runtime state to it.
 * - setRuntimeEnabled() changes the runtime state only — it never writes
 *   authored data.
 * - Implementations define their own disable semantics (Billboard applies its
 *   onExit behavior; Tether stops in place and re-runs its delay on
 *   re-enable).
 */
export interface RuntimeToggleable {
    /** Switch the behavior's runtime effect on or off. Play-mode restart resets to the authored state. */
    setRuntimeEnabled(enabled: boolean): void;
    /** Current runtime state. */
    isRuntimeEnabled(): boolean;
}

/**
 * Shared state machine for RuntimeToggleable implementations — owns the
 * runtime-enabled flag plus the play-start latch, so every behavior gets
 * identical pending/reset semantics instead of a hand-copied boilerplate.
 *
 * The latch (F31, Scene-Starts race): an enable/disable can arrive BEFORE
 * the behavior's own play-start reset runs (e.g. a Scene Starts trigger
 * firing in the same play-start window). While the session is not live the
 * value is latched, and `reset()` consumes it instead of stomping it with
 * the authored state — deterministic in both orderings, and never leaked
 * into the next session (`reset()` and `clearPending()` both drop it).
 */
export class RuntimeToggleLatch {
    private _enabled = true;
    private _pendingEnabled: boolean | null = null;

    /** Current runtime state. */
    get enabled(): boolean {
        return this._enabled;
    }

    /**
     * Record a runtime enable/disable.
     *
     * @param enabled - The requested state (applies immediately either way).
     * @param sessionLive - Whether the behavior's play session is live (its
     *   frame observer installed). When false, the value is also latched for
     *   the upcoming play-start reset to consume.
     * @returns True when the state actually changed — callers use this to
     *   reset their clocks/hysteresis exactly once per toggle.
     */
    set(enabled: boolean, sessionLive: boolean): boolean {
        if (!sessionLive) {
            // Play-start reset hasn't run yet — latch for it to consume
            this._pendingEnabled = enabled;
        }
        if (this._enabled === enabled) return false;
        this._enabled = enabled;
        return true;
    }

    /**
     * Play-start reset: back to the authored state — unless a toggle already
     * landed this play-start window, which is consumed rather than stomped.
     */
    reset(authoredEnabled: boolean): void {
        this._enabled = this._pendingEnabled ?? authoredEnabled;
        this._pendingEnabled = null;
    }

    /** Session teardown: drop any latched value so it can't leak into the next session. */
    clearPending(): void {
        this._pendingEnabled = null;
    }
}

/** Type guard for the RuntimeToggleable capability. */
export function isRuntimeToggleable(obj: unknown): obj is RuntimeToggleable {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        typeof (obj as RuntimeToggleable).setRuntimeEnabled === 'function' &&
        typeof (obj as RuntimeToggleable).isRuntimeEnabled === 'function'
    );
}
